import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, resolveImage, hexToRgba } from '../utils';
import SectionDivider from './SectionDivider';
import SectionBgImage from './SectionBgImage';

const ALIGNS = { left: 'text-left', center: 'text-center', right: 'text-right' };

export default function TextSection({ block, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    if (!content.title && !content.body && !(content.items?.length > 0)) return null;

    const anim = getAnimation(config, visible);
    const variant = config.variant || 'simple';
    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    // ── Quote ──
    if (variant === 'quote') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="max-w-3xl mx-auto text-center">
                        <svg className="w-12 h-12 mx-auto mb-6 opacity-20" style={{ color: s.accent }} fill="currentColor" viewBox="0 0 24 24">
                            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                        </svg>
                        {content.body && <p className="text-2xl md:text-3xl font-light leading-relaxed italic" style={{ color: s.text }}>{content.body}</p>}
                        {content.title && <p className="mt-6 text-base font-semibold" style={{ color: s.accent }}>— {content.title}</p>}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Steps ──
    if (variant === 'steps') {
        const items = content.items || [];
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: s.text }}>{content.title}</h2>}
                    <div className="max-w-4xl mx-auto space-y-0">
                        {items.map((item, i) => (
                            <div key={i} className={`flex gap-6 items-start transition-all duration-500 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
                                style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms' }}>
                                <div className="shrink-0 flex flex-col items-center">
                                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: s.accent }}>
                                        {i + 1}
                                    </div>
                                    {i < items.length - 1 && <div className="w-px h-16 mt-2" style={{ backgroundColor: hexToRgba(s.accent, 0.19) }} />}
                                </div>
                                <div className="pb-10">
                                    <h3 className="text-lg font-bold" style={{ color: s.text }}>{item.title}</h3>
                                    {item.description && <p className="text-sm mt-1 leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Side title ──
    if (variant === 'side-title') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-16 items-start">
                        <div className="lg:sticky lg:top-32">
                            {content.title && <h2 className="text-3xl md:text-4xl font-bold leading-tight" style={{ color: s.text }}>{content.title}</h2>}
                        </div>
                        <div className="lg:col-span-2">
                            {content.body && <div className="text-lg leading-relaxed whitespace-pre-line" style={{ color: s.textSub }}>{content.body}</div>}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Boxed grid ──
    if (variant === 'boxed-grid') {
        const items = content.items || [];
        const boxedColumns = config.columns || 3;
        const boxedColClass = boxedColumns === 2 ? 'md:grid-cols-2' : boxedColumns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ color: s.text }}>{content.title}</h2>}
                    <div className={`grid grid-cols-1 ${boxedColClass} gap-6`}>
                        {items.map((item, i) => (
                            <div key={i} className={`rounded-2xl p-6 transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ backgroundColor: hexToRgba(s.accent, 0.03), transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                {item.icon && <div className="text-2xl mb-3">{item.icon}</div>}
                                <h3 className="text-base font-bold mb-2" style={{ color: s.text }}>{item.title}</h3>
                                {item.description && <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── With image ──
    const imageUrl = resolveImage(content, 'image', mediaFolders) || content.image_url;
    if (variant === 'with-image' && imageUrl) {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className={ALIGNS[config.align] || 'text-left'}>
                            {content.title && <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: s.text }}>{content.title}</h2>}
                            {content.body && <div className="leading-relaxed text-lg whitespace-pre-line" style={{ color: s.textSub }}>{content.body}</div>}
                        </div>
                        <div><img src={imageUrl} alt={content.title || ''} className="w-full rounded-2xl shadow-lg object-cover" loading="lazy"/></div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Highlight ──
    if (variant === 'highlight') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="rounded-2xl p-8 md:p-12 border-l-4" style={{ backgroundColor: hexToRgba(s.accent, 0.03), borderColor: s.accent }}>
                        {content.title && <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: s.text }}>{content.title}</h2>}
                        {content.body && <div className="leading-relaxed text-lg whitespace-pre-line" style={{ color: s.textSub }}>{content.body}</div>}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Columns ──
    if (variant === 'columns') {
        const colCls = (config.columns || 2) === 3 ? 'md:columns-3' : 'md:columns-2';
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center" style={{ color: s.text }}>{content.title}</h2>}
                    {content.body && <div className={`${colCls} gap-8 leading-relaxed text-lg whitespace-pre-line`} style={{ color: s.textSub }}>{content.body}</div>}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Simple (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
            <div ref={ref} className={`relative z-10 mx-auto px-4 sm:px-6 lg:px-8 ${s.maxWidth} ${ALIGNS[config.align] || 'text-left'} ${anim}`}>
                {content.title && <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: s.text }}>{content.title}</h2>}
                {content.body && <div className="leading-relaxed text-lg whitespace-pre-line" style={{ color: s.textSub }}>{content.body}</div>}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
